import { LitElement, html, css, styleMap, Swiper, Navigation } from '@hh/global.js'

let myHeaders = new Headers()
myHeaders.append('Content-Type', 'application/graphql')

export class CollectionFilters extends LitElement {
  static properties = {
    data: { type: Array },
    activeFilter: { type: String },
    activeVariants: { type: Array },
    quickViewLoc: { type: Number },
    quickViewContainer: { type: Number },
    observer: { type: Object },
    updateCount: { type: Number },
    swiperSliders: { type: NodeList },
  }

  constructor() {
    super()

    Swiper.use([Navigation]);

    this.data = []
    this.collectionHandle = this.dataset.collectionHandle
    this.collectionHandles = this.getAttribute('data-collections').split(',')
    this.activeFilter = this.collectionHandles[0] || 'all'
    this.activeVariants = []
    this.quickViewLoc = 0
    this.quickViewContainer = 0
    this.observer = {}
    this.updateCount = 0
    this.collectionTabSwiper = ''
    this.swiperSliders = []

    this.metaobjectIDs = this.getAttribute('data-metaobject-ids')
    this.api = this.getAttribute('data-storefront-api')
    this.url = this.getAttribute('data-storefront-url')
    this.countryRate = parseFloat(Shopify.currency.rate)
    this.countryActive = Shopify.currency.active
    this.swiper = ''

    myHeaders.append('X-Shopify-Storefront-Access-Token', `${this.api}`)
    this.getMetaobjectsData(this.metaobjectIDs)

    this.attachShadow({ mode: 'open' })
    this.render()

    this.swiper = new Swiper(this.querySelector('.js-slider'), {
      slidesPerView: 'auto',
      spaceBetween: 24,
      allowSlidePrev: true,
      allowSlideNext: true,
      navigation: {
        nextEl: this.querySelector('.js-next'),
        prevEl: this.querySelector('.js-prev'),
      }
    })
  }

  connectedCallback() {
    super.connectedCallback()
  }

  updated(changedProperties) {
    if (
      (changedProperties.has('activeFilter') ||
        changedProperties.has('updateCount')) &&
      this.activeFilter !== 'all'
    ) {
      this.observeCards()
    }
  }

  observeCards() {
    const activeCards = this.shadowRoot.querySelectorAll(
      '.collection-shown .collection-product'
    )

    let intersectionOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            this.loadMoreProducts()

            this.observer?.unobserve(activeCards[activeCards.length - 1])
          }, 500)
        }
      })
    }, intersectionOptions)

    this.observer?.observe(activeCards[activeCards.length - 1])
  }

  loadMoreProducts() {
    const collectionData = this.data
      .filter((node) => node.handle === this.activeFilter)
      .map((node) => node.products)[0]

    const {
      pageInfo: { hasNextPage, endCursor },
    } = collectionData

    if (!hasNextPage) return

    this.getMoreProducts(endCursor)
  }

  async getImageViaGID(gid) {
    const raw = `query {
      node(id: "${gid}") {
        id
        ... on MediaImage {
          image {
            url (transform:{maxHeight: 300, maxWidth: 300})
          }
        }
      }
    }`

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    }

    const response = await fetch(
      `https://${this.url}/api/2023-07/graphql.json`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        const {
          data: {
            node: { image: url },
          },
        } = result

        return url
      })
      .catch((error) => console.log('error', error))

    return response.url
  }

  async getMoreProducts(cursor) {
    const raw = `query {
      collection(handle: "${this.activeFilter}") {
        title
        products (first: 6, after: "${cursor}") {
            pageInfo {
              endCursor
              hasNextPage
            }
            nodes {
              handle
              title
              onlineStoreUrl
              productType
              description
              tags
              vendor
              featuredImage {
                id
                url
              }
              images (first: 2) {
                nodes {
                  id
                  url (transform: { maxHeight: 300, maxWidth: 300 })
                  altText
                }
              }
              priceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants (first: 100) {
                nodes {
                  id
                  title
                  image {
                    id
                    url (transform: { maxHeight: 150, maxWidth: 150 })
                    altText
                  }
                }
              }
            }
        }
      }
    }`

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    }

    const response = await fetch(
      `https://${this.url}/api/2023-07/graphql.json`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        const {
          data: { collection },
        } = result

        return collection
      })
      .catch((error) => console.log('error', error))
    const {
      products: { nodes: addedProducts, pageInfo },
    } = response

    const collectionIndex = this.data.findIndex(
      (collection) => collection.handle === this.activeFilter
    )
    this.data[collectionIndex].products.nodes = [
      ...this.data[collectionIndex].products.nodes,
      ...addedProducts,
    ]
    this.data[collectionIndex].products.pageInfo.endCursor = pageInfo.endCursor
    this.data[collectionIndex].products.pageInfo.hasNextPage =
      pageInfo.hasNextPage
    // Trigger update to observe card
    this.updateCount++
    this.requestUpdate()
  }

  async getMetaobjectsData(gids) {
    const ids = JSON.stringify(gids.split(','))

    const raw = `query {
      nodes(ids: ${ids}) {
        id
        ... on Collection {
          handle
          title
          products (first: 12) {
            pageInfo {
              endCursor
              hasNextPage
            }
            nodes {
              handle
              title
              onlineStoreUrl
              productType
              description
              tags
              vendor
              featuredImage {
                id
                url
              }
              images (first: 2) {
                nodes {
                  id
                  url (transform: { maxHeight: 750, maxWidth: 750 })
                  altText
                }
              }
              priceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants (first: 100) {
                nodes {
                  id
                  title
                  image {
                    id
                    url (transform: { maxHeight: 150, maxWidth: 150 })
                    altText
                  }
                }
              }
            }
          }
        }
      }
    }`

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    }

    const response = await fetch(
      `https://${this.url}/api/2023-07/graphql.json`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        const {
          data: { nodes },
        } = result

        return nodes
      })
      .catch((error) => console.log('error', error))

    this.data = response
    this.addActionSwiperTab()
  }

  addActionSwiperTab() {
    const slot = this.shadowRoot.querySelector('slot[name="tab"]')
    const swiperEl = slot.assignedElements({ flatten: true })
    const swiperSliders = swiperEl[0].querySelectorAll('.swiper-slide')
    this.swiperSliders = swiperSliders

    swiperSliders.forEach((slider) => {
      if (slider.getAttribute('data-gid') === 'all') {
        slider.setAttribute('data-collection-handle', 'all')
        slider.addEventListener('click', () => this.tabChange('all', true))
        return
      }

      this.data
        .filter(
          (collection) =>
            slider.getAttribute('data-gid') ===
            collection.id.replace('gid://shopify/Collection/', '')
        )
        .map((collection) => {
          const { handle, title } = collection
          slider.innerHTML = `<span>${this.collectionHandle === handle ? 'ALL' : title}</span>`
          slider.setAttribute('data-collection-handle', handle)
          slider.addEventListener('click', () => this.tabChange(handle, true))
        })[0]
    })
  }

  getCurrencySymbol(currencyCode) {
    try {
      const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      })
      const parts = formatter.formatToParts(1)
      const currencySymbol = parts.find(
        (part) => part.type === 'currency'
      ).value
      return currencySymbol
    } catch (error) {
      // If the currency code is not supported or there's an error, return undefined.
      return undefined
    }
  }

  getPriceInCurrency(price, currency) {
    if (this.countryActive === currency) {
      return html`${this.getCurrencySymbol(currency)} ${parseFloat(price).toFixed(2)}`
    }

    const countryPrice = parseFloat(price) * this.countryRate

    return html`${this.getCurrencySymbol(this.countryActive)} ${countryPrice.toFixed(2)}`
  }

  handleizeName(name) {
    let handleizedName = name.toLowerCase()
    handleizedName = handleizedName.replace(/[^a-z0-9\s]/g, '')
    handleizedName = handleizedName.replace(/\s+/g, '-')

    return handleizedName
  }

  resetVariantsContainer() {
    const activeQuickView = this.shadowRoot.querySelector('.quick-view-shown')
    const activeQuickViewCard =
      this.shadowRoot.querySelector('.quick-view-active')
    activeQuickView?.classList.remove('quick-view-shown')
    activeQuickViewCard?.classList.remove('quick-view-active')
  }

  renderVariants(e, collectionIndex, productIndex) {
    this.resetVariantsContainer()

    const eventTarget = e.composedPath()[0]

    this.quickViewLoc =
      eventTarget.closest('.collection-product').offsetTop +
      eventTarget.closest('.collection-product').clientHeight
    this.activeVariants =
      this.data[collectionIndex].products.nodes[productIndex].variants.nodes

    eventTarget
      .closest('.collection-products')
      .classList.add('quick-view-shown')
    eventTarget
      .closest('.collection-product')
      .classList.add('quick-view-active')
  }

  tabChange(handle, swiper = false) {
    this.activeFilter = handle
    this.resetQuickView()

    if (swiper) {
      this.swiperSliders.forEach((slide) => {
        slide.getAttribute('data-collection-handle') === this.activeFilter
          ? slide.classList.add('is-active')
          : slide.classList.remove('is-active')
      })
    }

    const header = document.querySelector('hh-sticky-header') || 0
    window.scrollTo({
      top: this.offsetTop - (header.clientHeight + 30),
      behavior: 'smooth',
    })
  }

  resetQuickView() {
    this.activeVariants = []
    this.quickViewLoc = 0
    this.quickViewContainer = 0
    this.resetVariantsContainer()
  }

  render() {
    const quickViewStyle = {
      top: this.quickViewLoc + 'px',
    }

    return html`
      <div class="collection-filters__wrapper">
        <slot name="tab"></slot>
        <div
          class="collection-filters__products products--all"
          active-filter="${this.activeFilter}"
        >
          ${this.data.map((collection, _indexCollection) => {
            const {
              products: { nodes: products },
            } = collection

            return html`<div
              class="collection-products ${this.activeFilter ===
                collection.handle || this.activeFilter === 'all'
                ? 'collection-shown'
                : ''}"
              data-collection="${collection.handle}"
            >
              ${products.map((product, _indexProduct) => {
                  if (this.activeFilter === 'all' && _indexProduct + 1 > 3) return
                  const isSpecialProduct = product.title.includes('*');
                  if (isSpecialProduct) {
                    return html`<div class="collection-product">
                    <div class="collection-product__images  collection-product__images--espot">
                      ${product.images.nodes.map((image, i) => {
                        return html`<img
                          src="${image.url}"
                          srcset="${image.url} 750w"
                          loading="lazy"
                          alt="${image.altText}"
                          class="${i === 0 ? 'primary--espot' : 'secondary--espot'}"
                        />`
                      })}
                    </div>
 
                  </div>`
                  } else { 
                      return html`<div class="collection-product">
                        <div class="collection-product__images">
                          ${product.images.nodes.map((image, i) => {
                            return html`<img
                              src="${image.url}"
                              srcset="${image.url} 750w"
                              loading="lazy"
                              alt="${image.altText}"
                              class="${i === 0 ? 'primary' : 'secondary'}"
                            />`
                          })}
                        </div>
      
                        <div class="collection-product__content">
                          <div class="product-content--title">
                            <p>${product.title}</p>
                          </div>
                          <div
                            class="product-content--price ${product.priceRange
                              .maxVariantPrice.amount ===
                            product.priceRange.minVariantPrice.amount
                              ? ''
                              : 'is-discounted'}"
                          >
                            ${product.priceRange.maxVariantPrice.amount ===
                            product.priceRange.minVariantPrice.amount
                              ? ''
                              : html` <p class="discounted-price">
                                  ${this.getPriceInCurrency(
                                    product.priceRange.minVariantPrice.amount,
                                    product.priceRange.minVariantPrice.currencyCode
                                  )}
                                </p>`}
                            <p class="original-price">
                              ${this.getPriceInCurrency(
                                product.priceRange.maxVariantPrice.amount,
                                product.priceRange.maxVariantPrice.currencyCode
                              )}
                            </p>
                          </div>
      
                          <a
                            class="button product-content--cta"
                            href="${product.onlineStoreUrl}"
                          >
                            SHOP NOW
                          </a>
                        </div>
                        <div
                          class="collection-product__drawer--close"
                          @click="${() => {
                            this.resetQuickView()
                          }}"
                        >
                          —
                        </div>
                      </div>`
                  }
              })}

              <div
                class="collection-product__quickview"
                style=${styleMap(quickViewStyle)}
              >
                <div class="quickview__variants">
                  ${this.activeVariants.map((variant) => {
                    return html`<div class="quickview__variant">
                      <div class="quickview__variant--image">
                        <img
                          height="150"
                          width="150"
                          src="${variant.image.url}"
                          srcset="${variant.image.url} 150w"
                          loading="lazy"
                          alt="${variant.image.altText}"
                        />
                      </div>
                      <div class="quickview__variant--title">
                        ${variant.title}
                      </div>
                    </div>`
                  })}
                </div>
              </div>
            </div> `
          })}
        </div>
      </div>
    `
  }

  static styles = css`
    .collection-filters__wrapper {
      margin-bottom: 50px;
      max-width: 1300px;
      margin: 0 auto;
    }

    .collection-filters {
      list-style: none;
      padding: 0;
      display: flex;
      justify-content: center;
      text-align: center;
      margin-bottom: 50px;
    }

    .collection-filter {
      width: 100%;
      cursor: pointer;
      margin-top: 10px;
      margin-bottom: 10px;
    }

    .collection-filters__products {
      position: relative;
    }

    .collection-filter span {
      display: block;
      padding: 5px 15px;
      border-radius: 20px;
    }

    .collection-filter.is-active span {
      background: #000;
      color: #fff;
    }

    .collection-products {
      display: none;
      width: 100%;
      justify-content: center;
      flex-wrap: wrap;
    }

    .collection-shown {
      display: flex;
      gap: 10px;
    }

    .collection-shown:last-of-type::after {
      display: none;
    }

    .collection-products::after {
      content: ' ';
      width: 100%;
      border-bottom: 1px solid rgb(0 0 0 / 20%);
      flex: 0 0 100%;
      margin-top: 0;
      margin-bottom: 50px;
    }

    .collection-product {
      display: flex;
      max-width: calc(100% / 3);
      width: 100%;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      padding: 0;
      position: relative;
    }

    .collection-product__images {
      width: auto;
      position: relative;
    }

    .collection-product__images--espot {
        height: 100%;
    }

    .collection-product__images > img {
      width: 100%;
      max-width: 399px;
      height: 100%;
      max-height: 399px;
    }

    .collection-product__images > img.primary--espot,.collection-product__images > img.secondary--espot {
      width: 100%;
      height: 100%;
      max-height: 100%;
      object-fit: cover;
    }

    .collection-product__images > img:nth-child(2) {
      position: absolute;
      opacity: 0;
      left: 0;
      top: 0;
      transition: opacity 0.2s;
    }

    .collection-product__images:hover > img:nth-child(1) {
      opacity: 0;
      transition: opacity 0.2s;
    }

    .collection-product__images:hover > img:nth-child(2) {
      opacity: 1;
      transition: opacity 0.2s;
    }

    .collection-product__content {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
    }

    .product-content--title p {
      font-size: 21px;
      line-height: 23px;
      font-family: var(--hh-font-family-courier);
      margin-bottom: 10px;
      margin-top: 0;
    }

    .product-content--description {
      overflow: hidden;
      width: 100%;
      text-overflow: ellipsis;
      white-space: nowrap;

      font-size: 14px;
      line-height: 21px;
    }

    .product-content--price {
      display: flex;
      justify-content: center;
    }

    .product-content--price p {
      margin: 0 5px;
      color: var(--hh-color-foreground);
      font-size: 1.4rem;
      line-height: 2.1rem;
    }

    .product-content--price.is-discounted .original-price {
      text-decoration: line-through;
    }

    .product-content--cta {
      margin-top: 20px;
      background-color: #000;
      border: 1px solid #000;
      color: #fff;
      width: 100%;
      max-width: 200px;
      margin: 20px auto 20px;
      text-decoration: none;

      font-size: 12px;
      font-weight: 400;
      letter-spacing: 0.75px;
      text-transform: uppercase;
      margin-top: 20px;
      padding: 10px;
      transition: background-color 0.2s, color 0.2s;
    }

    .product-content--cta:hover {
      color: #000;
      background-color: #fff;
      transition: background-color 0.2s, color 0.2s;
    }

    .quick-view-active.collection-product {
      padding-bottom: 260px;
    }

    .collection-product__quickview {
      display: none;
    }

    .collection-product__quickview::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
      background-color: #f5f5f5;
    }

    .collection-product__quickview::-webkit-scrollbar {
      width: 6px;
      height: 6px;
      background-color: #f5f5f5;
    }

    .collection-product__quickview::-webkit-scrollbar-thumb {
      background-color: #000000;
    }

    .quick-view-shown .collection-product__quickview {
      display: block;
      padding-bottom: 20px;
      padding-top: 20px;
      max-height: 200px;
    }

    .collection-product__drawer--close {
      background: #000;
      position: absolute;
      bottom: 260px;
      transform: translateY(50%);
      border-radius: 20px;
      z-index: 5;
      justify-content: center;
      align-items: center;
      font-size: 20px;
      padding: 6px;
      width: auto;
      height: auto;
      line-height: 1;
      color: #fff;
      cursor: pointer;
      display: none;
    }

    .quick-view-active .collection-product__drawer--close {
      display: inline-flex;
    }

    .collection-product__quickview {
      border-top: 1px solid rgba(0, 0, 0, 0.2);
      position: absolute;
      width: 100%;
      overflow-x: scroll;
      overflow-y: hidden;
    }

    .quickview__variants {
      display: flex;
      flex-wrap: nowrap;
      width: 100%;
      gap: 20px;
    }

    .quickview__variant {
      max-height: 200px;
      text-overflow: ellipsis;
    }

    .quickview__variant--title {
      text-align: center;
      margin-top: 5px;
    }

    .collection-filters__products:not([active-filter='all'])
      .collection-shown
      > .collection-product {
      border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    }

    .collection-filters__products:not([active-filter='all'])
      .collection-shown::after {
      display: none;
    }

    .swiper {
      width: 100%;
      height: 100%;
      overflow: hidden;
    }

    @media screen and (max-width: 1200px) {
      .collection-products {
        gap: 10px;
      }

      .collection-product__images > img {
        width: 100%;
      }

      .collection-product {
        max-width: calc(100% / 2 - 20px);
      }
    }

    @media screen and (max-width: 770px) {
      .collection-products {
        gap: 0;
      }

      .collection-product__images > img {
        width: 100%;
      }

      .collection-product {
        max-width: 100%;
      }
    }
  `
}

customElements.define('hh-collection-filters', CollectionFilters)
